import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Menu, X, LogOut, LayoutDashboard, ChevronDown, Gift, PlusCircle, Image, Briefcase } from "lucide-react";
import InstallPWAButton from "./InstallPWAButton";
import NotificationBell from "./NotificationBell";
import ChatNavBell from "./ChatNavBell";
import MobileMenuDrawer from "./MobileMenuDrawer";
import { isServiceRole } from "../lib/roles";

const LOGO = "/gpm-logo.png";

/* Primary nav — split into always-visible + overflow (More dropdown until 2xl) */
const primaryNav = [
    { to: "/properties",   label: "Properties" },
    { to: "/new-projects", label: "New Projects" },
    { to: "/auctions",     label: "Auctions" },
    { to: "/services",     label: "Services" },
    { to: "/builders",     label: "Builders" },
];
const overflowNav = [
    { to: "/loan-services", label: "Loans" },
    { to: "/calculators",   label: "Calculators" },
    { to: "/guides",        label: "Guides" },
    { to: "/pricing",       label: "Pricing" },
    { to: "/refer",         label: "Refer & Earn", accent: true },
];

/* Helper for consistent data-testids */
const kebab = (s) => s.toLowerCase().replace(/\s+|&/g, "-");

/* Small "More ▾" dropdown */
function MoreDropdown() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="nav-more-btn"
                className="inline-flex items-center gap-1 text-sm font-medium text-white/80 hover:text-gold-500 transition-colors"
            >
                More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div
                    data-testid="nav-more-panel"
                    className="absolute right-0 mt-3 w-48 bg-white rounded-lg shadow-2xl border border-navy-500/10 overflow-hidden z-50"
                >
                    {overflowNav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-${kebab(n.label)}`}
                            className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-gold-500/10 text-gold-700"
                                        : n.accent
                                            ? "text-gold-600 hover:bg-gold-500/5"
                                            : "text-navy-500 hover:bg-gold-500/5 hover:text-gold-700"
                                }`
                            }
                        >
                            {n.accent && <span aria-hidden>🎁 </span>}{n.label}
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Navbar() {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const onLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <header className="sticky top-0 z-50 bg-navy-900/90 backdrop-blur-xl border-b border-white/10" data-testid="main-navbar">
            {/* Container widens on larger screens so 1440px+ users get more breathing room */}
            <div className="mx-auto px-4 lg:px-6 xl:px-8 h-20 flex items-center gap-4 lg:gap-6 xl:gap-8 max-w-[1920px]">

                {/* ── LEFT: Logo block (fixed-width to prevent overlap) ── */}
                <Link
                    to="/"
                    className="shrink-0 flex items-center gap-3 group"
                    data-testid="brand-logo"
                >
                    <div className="w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center shrink-0">
                        <img
                            src={LOGO}
                            alt="Global Property Mart"
                            className="w-full h-full object-contain drop-shadow-[0_0_16px_rgba(218,165,32,0.4)]"
                        />
                    </div>
                    <div className="leading-tight hidden sm:block whitespace-nowrap">
                        <div className="font-heading font-bold text-white text-lg lg:text-xl">
                            Global Property Mart
                        </div>
                        <div className="text-[9px] lg:text-[10px] tracking-[0.24em] uppercase text-gold-500 font-semibold mt-0.5">
                            One Platform · Every Need
                        </div>
                    </div>
                </Link>

                {/* ── CENTER: Primary nav ── */}
                <nav className="hidden lg:flex flex-1 items-center justify-center gap-1 xl:gap-2 min-w-0">
                    {primaryNav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${kebab(n.label)}`}
                            className={({ isActive }) =>
                                `whitespace-nowrap px-2.5 xl:px-3 py-2 rounded-md text-[13px] xl:text-sm font-medium transition-colors ${
                                    isActive
                                        ? "text-gold-500 bg-gold-500/5"
                                        : "text-white/85 hover:text-gold-500 hover:bg-white/5"
                                }`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                    {/* Overflow items inline on 2xl+, in "More" dropdown otherwise. Excludes accent CTAs. */}
                    <div className="hidden 2xl:flex items-center gap-2">
                        {overflowNav.filter((n) => !n.accent).map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                data-testid={`nav-${kebab(n.label)}`}
                                className={({ isActive }) =>
                                    `whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                        isActive ? "text-gold-500 bg-gold-500/5" : "text-white/85 hover:text-gold-500 hover:bg-white/5"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </div>
                    <div className="2xl:hidden">
                        <MoreDropdown />
                    </div>
                </nav>

                {/* ── RIGHT: Actions ── */}
                <div className="hidden lg:flex items-center gap-1.5 xl:gap-2 2xl:gap-3 shrink-0">
                    {/* Primary conversion CTA — swaps to "My Portfolio" for service professionals
                        (they never list properties, they showcase work). */}
                    {user && isServiceRole(user.role) ? (
                        <Link
                            to="/my-portfolio"
                            data-testid="nav-my-portfolio"
                            className="inline-flex items-center gap-1 pl-2.5 pr-3 py-1.5 rounded-full border-2 border-gold-500/70 bg-gradient-to-r from-gold-500/15 to-amber-500/10 text-white text-[12px] xl:text-[13px] font-extrabold hover:border-gold-500 hover:from-gold-500/25 transition-colors whitespace-nowrap"
                        >
                            <Image className="w-3.5 h-3.5 text-gold-400" />
                            <span className="hidden xl:inline">My Portfolio</span>
                            <span className="xl:hidden">Portfolio</span>
                        </Link>
                    ) : (
                        <>
                            <Link
                                to={user ? "/add-property" : "/register?intent=sell"}
                                data-testid="nav-post-property"
                                className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1.5 rounded-full border-2 border-gold-500/70 bg-gradient-to-r from-gold-500/15 to-amber-500/10 text-white text-[12px] xl:text-[13px] font-extrabold hover:border-gold-500 hover:from-gold-500/25 transition-colors whitespace-nowrap"
                            >
                                <PlusCircle className="w-3.5 h-3.5 text-gold-400" />
                                <span className="hidden xl:inline">Post Property</span>
                                <span className="xl:hidden">Post</span>
                                <span className="px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-extrabold uppercase tracking-widest">FREE</span>
                            </Link>
                            <Link
                                to="/register?intent=service"
                                data-testid="nav-post-service"
                                title="Loan Agent, Advocate, Architect, Interior Designer, Civil Contractor, Engineer & more"
                                className="inline-flex items-center gap-1 pl-2.5 pr-3 py-1.5 rounded-full border-2 border-emerald-500/60 bg-gradient-to-r from-emerald-500/15 to-teal-500/10 text-white text-[12px] xl:text-[13px] font-extrabold hover:border-emerald-500 hover:from-emerald-500/25 transition-colors whitespace-nowrap"
                            >
                                <Briefcase className="w-3.5 h-3.5 text-emerald-400" />
                                <span className="hidden xl:inline">Post Service</span>
                                <span className="xl:hidden">Service</span>
                            </Link>
                        </>
                    )}

                    {/* Refer & Earn — pill CTA (2xl+ only, moves to More at smaller sizes) */}
                    <NavLink
                        to="/refer"
                        data-testid="nav-refer-earn"
                        className={({ isActive }) =>
                            `hidden 2xl:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-bold border transition-all ${
                                isActive
                                    ? "bg-gold-500 text-navy-900 border-gold-500"
                                    : "border-gold-500/50 text-gold-400 hover:bg-gold-500 hover:text-navy-900 hover:border-gold-500"
                            }`
                        }
                    >
                        <Gift className="w-3.5 h-3.5" /> Refer & Earn
                    </NavLink>

                    {/* Install App */}
                    <InstallPWAButton
                        variant="compact"
                        className="hidden xl:inline-flex !text-gold-400 !border-gold-500/40 !bg-transparent hover:!bg-gold-500/10 hover:!text-gold-300"
                    />

                    {/* Notification bell (logged-in) */}
                    {user && <NotificationBell variant="desktop" />}

                    {/* Chat bell — all logged-in users, opens /chat which enforces paid gate */}
                    {user && <ChatNavBell />}

                    {/* Vertical divider */}
                    {user && <span className="hidden 2xl:inline-block h-6 w-px bg-white/15 mx-1" />}

                    {/* Auth section */}
                    {user ? (
                        <>
                            <Link
                                to={isServiceRole(user.role) ? "/my-services" : "/dashboard"}
                                data-testid="nav-dashboard"
                                title={isServiceRole(user.role) ? "My Services" : "Dashboard"}
                                className="inline-flex items-center gap-1.5 px-2 xl:px-3 py-1.5 rounded-md text-[13px] font-medium text-white/90 hover:text-gold-500 hover:bg-white/5"
                            >
                                <LayoutDashboard className="w-4 h-4" /> <span className="hidden 2xl:inline">{isServiceRole(user.role) ? "My Services" : "Dashboard"}</span>
                            </Link>
                            <button
                                data-testid="nav-logout"
                                onClick={onLogout}
                                title="Logout"
                                className="inline-flex items-center gap-1.5 px-2 xl:px-3 py-1.5 rounded-md text-[13px] font-medium text-white/70 hover:text-white hover:bg-white/5"
                            >
                                <LogOut className="w-4 h-4" /> <span className="hidden 2xl:inline">Logout</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-login"
                                className="text-[13px] font-medium text-white/85 hover:text-gold-500 px-2 xl:px-3 py-1.5"
                            >
                                Login
                            </Link>
                            <Link
                                to="/register"
                                data-testid="nav-register"
                                className="px-3 xl:px-4 py-1.5 xl:py-2 rounded-md bg-gold-500 text-navy-900 text-[13px] font-extrabold hover:bg-gold-400 transition-colors whitespace-nowrap"
                            >
                                Get Started
                            </Link>
                        </>
                    )}
                </div>

                {/* ── MOBILE ROW ── keep it tight so the hamburger menu is always reachable.
                    The Post Property / Post Service CTAs live in the Mobile Hero (home page)
                    and inside the hamburger drawer — duplicating them here pushed the menu
                    button off-screen on narrow devices. */}
                <div className="lg:hidden ml-auto flex items-center gap-2 shrink-0">
                    {user && isServiceRole(user.role) && (
                        <Link
                            to="/my-portfolio"
                            data-testid="mobile-nav-portfolio"
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-gold-500/50 text-white bg-white/5 text-[11px] font-bold hover:bg-white/10"
                        >
                            <Image className="w-3 h-3 text-gold-400" />
                            Portfolio
                        </Link>
                    )}
                    {user && <NotificationBell variant="mobile" />}
                    <button
                        className="text-white p-1"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer — categorized navigation */}
            <MobileMenuDrawer open={open} onClose={() => setOpen(false)} />
        </header>
    );
}
